import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Check, Lock, TriangleAlert } from "lucide-react";
import { EASE, MARK, gbp } from "@/components/common";
import { WHATSAPP_DISPLAY } from "@/data/vehicles";
import { apiFetch } from "@/api";

const METHOD_LABELS = { klarna: "Klarna", afterpay_clearpay: "Clearpay", card: "Card" };

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ phase: "loading" });

  useEffect(() => {
    window.scrollTo(0, 0);
    const clientSecret = params.get("payment_intent_client_secret");
    const type = params.get("type") || "";
    const ref = params.get("ref") || "";
    const token = params.get("token") || "";
    if (!clientSecret || !type) {
      setState({ phase: "error", message: "This payment return link is missing its payment details." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cfg = await apiFetch("/payments/config");
        if (!cfg.enabled || !cfg.publishable_key) throw new Error("Payments are not configured.");
        const stripe = await loadStripe(cfg.publishable_key);
        let pi = null;
        for (let i = 0; i < 8; i++) {
          const r = await stripe.retrievePaymentIntent(clientSecret);
          pi = r.paymentIntent;
          if (!pi) throw new Error("Could not verify the payment — please contact us.");
          if (pi.status !== "processing") break;
          await new Promise((res) => setTimeout(res, 2000));
        }
        if (cancelled) return;
        if (pi.status === "requires_capture" || pi.status === "succeeded") {
          if (type === "rental") {
            const out = await apiFetch(`/bookings/${ref}/confirm-payment`, { method: "POST", body: { payment_intent_id: pi.id } });
            if (out.payment_status !== "authorised") {
              if (!cancelled) setState({ phase: "processing", type, token });
              return;
            }
            const b = await apiFetch(`/bookings/${ref}`);
            if (!cancelled) setState({ phase: "done", type, ref, total: b.total, vehicleName: b.vehicle_name });
          } else {
            const out = await apiFetch(`/recovery-pay/${token}/confirm`, { method: "POST", body: {} });
            const info = await apiFetch(`/recovery-pay/${token}`);
            if (!cancelled) setState({ phase: "done", type, ref: info.ref, total: info.estimated_total, method: info.payment_method || out.method || "" });
          }
        } else if (pi.status === "processing") {
          if (!cancelled) setState({ phase: "processing", type, token });
        } else {
          if (!cancelled) setState({ phase: "failed", type, token });
        }
      } catch (e) {
        if (!cancelled) setState({ phase: "error", message: e.message || "Could not confirm the payment." });
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  return (
    <div className="bg-[#050506] text-zinc-100 min-h-screen">
      <div className="vignette-overlay" />
      <header className="border-b border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center">
          <Link to="/" data-testid="return-home-link" className="flex items-center gap-3">
            <img src={MARK} alt="Senna Prestige Travels" className="h-8 w-auto" />
            <span className="hidden sm:block text-[0.6rem] tracking-[0.42em] uppercase text-zinc-300">Secure Payment</span>
          </Link>
        </div>
      </header>
      <main className="relative z-10 max-w-2xl mx-auto px-6 py-16 md:py-24">
        {state.phase === "loading" && (
          <p data-testid="return-loading" className="text-center text-zinc-500 font-light py-20">Verifying your payment…</p>
        )}

        {state.phase === "done" && (
          <motion.div data-testid="return-success" className="text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)] mb-8">
              <Check size={26} className="text-white" strokeWidth={1.5} />
            </span>
            {state.ref && <p className="kicker mb-4">Reference {state.ref}</p>}
            {state.type === "rental" ? (
              <>
                <h1 className="font-display text-3xl sm:text-4xl text-white">Booking Submitted for Approval</h1>
                <p className="mt-6 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-md mx-auto">
                  Your payment of {gbp(state.total)} is authorised only — captured once your booking is approved, and released in full if declined. Your documents have been received.
                </p>
              </>
            ) : (
              <>
                <h1 className="font-display text-3xl sm:text-4xl text-white">Payment Secured</h1>
                <p className="mt-6 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-md mx-auto">
                  The full recovery total of {gbp(state.total)} has been authorised{METHOD_LABELS[state.method] ? ` with ${METHOD_LABELS[state.method]}` : ""} — it is captured only when your recovery is completed, and released in full if it does not proceed.
                </p>
              </>
            )}
            <Link to="/" data-testid="return-success-home" className="btn-ghost justify-center mt-10 inline-flex">Return to Home</Link>
            <p className="mt-6 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500">Questions? Call or WhatsApp {WHATSAPP_DISPLAY}</p>
          </motion.div>
        )}

        {state.phase === "processing" && (
          <div data-testid="return-processing" className="text-center py-10">
            <h1 className="font-display text-3xl sm:text-4xl text-white">Payment Processing</h1>
            <p className="mt-6 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-md mx-auto">
              Your payment provider is still confirming this payment. This usually completes within a few minutes — we will email you as soon as it is confirmed. Please do not pay again.
            </p>
            <p className="mt-6 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500">Questions? Call or WhatsApp {WHATSAPP_DISPLAY}</p>
          </div>
        )}

        {(state.phase === "failed" || state.phase === "error") && (
          <div data-testid="return-failed" className="text-center py-10">
            <span className="inline-flex items-center justify-center w-16 h-16 rounded-full shadow-[inset_0_0_0_1px_rgba(252,165,165,0.4)] mb-8">
              <TriangleAlert size={24} className="text-red-300" strokeWidth={1.5} />
            </span>
            <h1 className="font-display text-3xl sm:text-4xl text-white">Payment Not Completed</h1>
            <p className="mt-6 text-sm md:text-base text-zinc-400 font-light leading-relaxed max-w-md mx-auto">
              {state.phase === "error" ? state.message : "The payment was not authorised. No money has been taken."}
            </p>
            {state.type === "recovery" && state.token && (
              <Link to={`/recovery/pay/${state.token}`} data-testid="return-retry-link" className="btn-primary justify-center mt-10 inline-flex">Return to Secure Payment</Link>
            )}
            <p className="mt-8 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500 flex items-center justify-center gap-2">
              <Lock size={12} /> Need help? Call or WhatsApp {WHATSAPP_DISPLAY}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
